import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import config from '../config/config.json';
import GiftCardModal from './GiftCardModal';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [values, setValues] = useState(Array(8).fill(''));
  const [isCorrect, setIsCorrect] = useState(null);
  const [snowflakes, setSnowflakes] = useState([]);
  const [sparkles, setSparkles] = useState([]);
  const [displayedText, setDisplayedText] = useState('');
  const [showPresents, setShowPresents] = useState(false);
  const [showCursor, setShowCursor] = useState(true);
  const [hoveredPresent, setHoveredPresent] = useState(null);
  const [clickedPresents, setClickedPresents] = useState([]);
  const [showGiftCard, setShowGiftCard] = useState(false);
  const [lastClickedTime, setLastClickedTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const inputRefs = useRef([]);

  const welcomeText = "Welcome Rushi";
  const passwordPrompt = "Enter the password, once you've unraveled the mystery of The Silent Storm.";
  const congratsText = "Congrats Rushi! This is your personal christmas tree!";
  const instructionText = "Choose wisely, and savor the spree – each present can only be clicked once, you see!";
  const cooldownStartedText = "Your gift is on the way! Come back soon for another present.";

  const presents = [
    { emoji: '🍣', id: 'sushi' },
    { emoji: '💄', id: 'lipstick' },
    { emoji: '🧸', id: 'teddy' },
    { emoji: '💃🏽', id: 'dancer' },
    { emoji: '🏋🏽‍♀️', id: 'workout' },
    { emoji: '🥂', id: 'drink' }
  ];

  useEffect(() => {
    // Create snowflakes
    const createSnowflakes = () => {
      const flakes = [];
      for (let i = 0; i < 50; i++) {
        flakes.push({
          id: i,
          left: Math.random() * 100,
          animationDuration: Math.random() * 3 + 5,
          animationDelay: Math.random() * 8,
          opacity: Math.random() * 0.5 + 0.3,
          fontSize: Math.random() * 10 + 10,
        });
      }
      setSnowflakes(flakes);
    };
    createSnowflakes();

    // Check if coming back from checkout/gift card/scrapbook
    const isAuthenticated = localStorage.getItem('authenticated') === 'true';
    if (location.state?.showTree && isAuthenticated) {
      setShowPresents(true);
      // Different message based on source
      if (location.state?.fromScrapbook) {
        // Don't set any message, let the normal typewriter animation play
        setDisplayedText('');
      } else if (location.state?.isGiftCard) {
        setDisplayedText("Come back soon for another present!");
        setShowCursor(false);
      } else {
        setDisplayedText(cooldownStartedText);
        setShowCursor(false);
      }
    }

    // Load clicked presents from API
    fetch('/api/clicked-presents/get')
      .then(res => res.json())
      .then(data => {
        if (data.clickedPresents) {
          setClickedPresents(data.clickedPresents);
        }
        if (data.lastClickedTime) {
          setLastClickedTime(data.lastClickedTime);
        }
      })
      .catch(err => console.error('Error loading clicked presents:', err));
  }, [location.state?.showTree, location.state?.fromScrapbook, location.state?.isGiftCard]);

  // Countdown timer effect
  useEffect(() => {
    if (!lastClickedTime) {
      setTimeRemaining(null);
      return;
    }

    const updateCountdown = () => {
      const now = Date.now();
      const elapsed = now - lastClickedTime;
      const cooldownPeriod = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      const remaining = cooldownPeriod - elapsed;

      if (remaining <= 0) {
        setTimeRemaining(null);
        setLastClickedTime(null);
      } else {
        setTimeRemaining(remaining);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [lastClickedTime]);

  useEffect(() => {
    // Skip typewriter if coming from checkout/gift card (but not scrapbook)
    if (location.state?.showTree && !location.state?.fromScrapbook) {
      return;
    }

    // Typewriter effect - changes based on showPresents state
    let currentIndex = 0;
    let phase = showPresents ? 'typing-congrats' : 'typing-welcome';

    const typeInterval = setInterval(() => {
      if (!showPresents) {
        // Password phase
        if (phase === 'typing-welcome') {
          if (currentIndex <= welcomeText.length) {
            setDisplayedText(welcomeText.slice(0, currentIndex));
            currentIndex++;
          } else {
            setTimeout(() => {
              phase = 'deleting-welcome';
              currentIndex = welcomeText.length;
            }, 1000);
          }
        } else if (phase === 'deleting-welcome') {
          if (currentIndex >= 0) {
            setDisplayedText(welcomeText.slice(0, currentIndex));
            currentIndex--;
          } else {
            phase = 'typing-prompt';
            currentIndex = 0;
          }
        } else if (phase === 'typing-prompt') {
          if (currentIndex <= passwordPrompt.length) {
            setDisplayedText(passwordPrompt.slice(0, currentIndex));
            currentIndex++;
          } else {
            clearInterval(typeInterval);
          }
        }
      } else {
        // Presents phase
        if (phase === 'typing-congrats') {
          if (currentIndex <= congratsText.length) {
            setDisplayedText(congratsText.slice(0, currentIndex));
            currentIndex++;
          } else {
            setTimeout(() => {
              phase = 'deleting-congrats';
              currentIndex = congratsText.length;
            }, 1500);
          }
        } else if (phase === 'deleting-congrats') {
          if (currentIndex >= 0) {
            setDisplayedText(congratsText.slice(0, currentIndex));
            currentIndex--;
          } else {
            phase = 'typing-instruction';
            currentIndex = 0;
          }
        } else if (phase === 'typing-instruction') {
          if (currentIndex <= instructionText.length) {
            setDisplayedText(instructionText.slice(0, currentIndex));
            currentIndex++;
          } else {
            setTimeout(() => {
              phase = 'deleting-instruction';
              currentIndex = instructionText.length;
            }, 1500);
          }
        } else if (phase === 'deleting-instruction') {
          if (currentIndex >= 0) {
            setDisplayedText(instructionText.slice(0, currentIndex));
            currentIndex--;
          } else {
            setShowCursor(false);
            clearInterval(typeInterval);
          }
        }
      }
    }, 50);

    return () => clearInterval(typeInterval);
  }, [showPresents, location.state?.showTree, location.state?.fromScrapbook]);

  const sha256 = async (message) => {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleChange = async (index, value) => {
    if (value.length > 1) return;

    const newValues = [...values];
    newValues[index] = value.toUpperCase();
    setValues(newValues);

    if (value && index < 7) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if all boxes are filled
    const allFilled = newValues.every(val => val !== '');

    if (allFilled) {
      // Join all values without the dash for hashing
      const enteredPassword = newValues.join('');

      // Check for special commands
      // XXXRESET - reset everything
      if (enteredPassword === 'XXXRESET') {
        setClickedPresents([]);
        setLastClickedTime(null);
        setTimeRemaining(null);
        setValues(Array(8).fill(''));
        setDisplayedText('State reset!');

        // Reset on server
        fetch('/api/clicked-presents/set', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clickedPresents: [], lastClickedTime: null })
        }).catch(err => console.error('Error resetting clicked presents:', err));

        setTimeout(() => {
          setDisplayedText('');
        }, 2000);
        return;
      }

      // TIMERXXX - reset only the countdown timer
      if (enteredPassword === 'TIMERXXX') {
        setLastClickedTime(null);
        setTimeRemaining(null);
        setValues(Array(8).fill(''));
        setDisplayedText('Timer reset!');

        // Reset timer on server (keep clicked presents)
        fetch('/api/clicked-presents/set', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clickedPresents, lastClickedTime: null })
        }).catch(err => console.error('Error resetting timer:', err));

        setTimeout(() => {
          setDisplayedText('');
        }, 2000);
        return;
      }

      const enteredHash = await sha256(enteredPassword);
      if (enteredHash === config.authKey) {
        setIsCorrect(true);
        setDisplayedText('');
        localStorage.setItem('authenticated', 'true');
        // Flash green for 500ms then show presents
        setTimeout(() => {
          setShowPresents(true);
        }, 500);
      } else {
        setIsCorrect(false);
        setDisplayedText('Wrong password!');
        setTimeout(() => setIsCorrect(null), 500);
      }
    }
  };

  const startCooldown = () => {
    const clickTime = Date.now();
    setLastClickedTime(clickTime);

    // Save to API
    fetch('/api/clicked-presents/set', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clickedPresents, lastClickedTime: clickTime })
    }).catch(err => console.error('Error saving cooldown:', err));
  };

  const handlePresentClick = (id) => {
    // Check if on cooldown
    if (timeRemaining !== null && timeRemaining > 0) {
      return; // Prevent clicking if on cooldown
    }

    // Add to clicked presents
    const updatedClicked = [...clickedPresents, id];
    setClickedPresents(updatedClicked);

    // Save clicked presents to API (without cooldown yet)
    fetch('/api/clicked-presents/set', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clickedPresents: updatedClicked, lastClickedTime })
    }).catch(err => console.error('Error saving clicked presents:', err));

    const clickedPresent = presents.find(p => p.id === id);

    // Different delivery times based on present
    let delivery;
    if (id === 'sushi' || id === 'drink') {
      delivery = '~1 hour (Call me for exact ETA)';
    } else if (id === 'workout') {
      delivery = '~10 seconds (It\'s in the email)';
    } else {
      delivery = 'Free Standard 5-7 Business Day Shipping';
    }

    // Handle workout separately - show gift card modal
    if (id === 'workout') {
      setShowGiftCard(true);
    } else if (id === 'teddy' || id === 'lipstick' || id === 'dancer' || id === 'sushi' || id === 'drink') {
      // Navigate to checkout for other presents
      navigate('/checkout', { state: { emoji: clickedPresent.emoji, id: id, delivery: delivery, startCooldown: true } });
    } else {
      // Other presents - to be implemented
      console.log('Clicked:', id);
    }
  };

  // Expose startCooldown to child components
  window.startCooldown = startCooldown;

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !values[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleMouseMove = (e) => {
    const newSparkle = {
      id: Date.now() + Math.random(),
      x: e.clientX,
      y: e.clientY,
    };
    setSparkles((prev) => [...prev, newSparkle]);

    setTimeout(() => {
      setSparkles((prev) => prev.filter((s) => s.id !== newSparkle.id));
    }, 1000);
  };

  return (
    <div className="App" onMouseMove={handleMouseMove}>
      {snowflakes.map((flake) => (
        <div
          key={flake.id}
          className="snowflake"
          style={{
            left: `${flake.left}%`,
            animationDuration: `${flake.animationDuration}s`,
            animationDelay: `${flake.animationDelay}s`,
            opacity: flake.opacity,
            fontSize: `${flake.fontSize}px`,
          }}
        >
          ❄
        </div>
      ))}

      {sparkles.map((sparkle) => (
        <div
          key={sparkle.id}
          className="sparkle"
          style={{
            left: `${sparkle.x}px`,
            top: `${sparkle.y}px`,
          }}
        >
          ✨
        </div>
      ))}

      {!showPresents ? (
        <div className="password-section">
          <div className="password-container">
            <div className="box-group">
              <input
                ref={(el) => (inputRefs.current[0] = el)}
                type="text"
                maxLength="1"
                className={`password-box ${isCorrect === true ? 'correct' : ''} ${isCorrect === false ? 'incorrect' : ''}`}
                value={values[0]}
                onChange={(e) => handleChange(0, e.target.value)}
                onKeyDown={(e) => handleKeyDown(0, e)}
                autoFocus
              />
            </div>
            <span className="dash">-</span>
            <div className="box-group">
              {[1, 2, 3, 4, 5, 6, 7].map((index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  maxLength="1"
                  className={`password-box ${isCorrect === true ? 'correct' : ''} ${isCorrect === false ? 'incorrect' : ''}`}
                  value={values[index]}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                />
              ))}
            </div>
          </div>
          <p className="prompt-text">{displayedText}<span className="cursor">|</span></p>
        </div>
      ) : (
        <div className="presents-section">
          <div className="header-text">
            {displayedText}
            {showCursor && <span className="cursor">|</span>}
          </div>

          {timeRemaining !== null && timeRemaining > 0 && (
            <div className="cooldown-timer">
              New present available in: {Math.floor(timeRemaining / (1000 * 60 * 60))}h {Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60))}m {Math.floor((timeRemaining % (1000 * 60)) / 1000)}s
            </div>
          )}

          <div className="presents-container">
            {presents.slice(0, 3).map((present) => {
              const isClicked = clickedPresents.includes(present.id);
              const isDisabled = timeRemaining !== null && timeRemaining > 0;
              return (
                <div
                  key={present.id}
                  className={`present-box ${isClicked ? 'clicked' : ''} ${isDisabled && !isClicked ? 'disabled' : ''}`}
                  onMouseEnter={() => !isClicked && !isDisabled && setHoveredPresent(present.id)}
                  onMouseLeave={() => setHoveredPresent(null)}
                  onClick={() => !isClicked && !isDisabled && handlePresentClick(present.id)}
                  style={{
                    opacity: isClicked ? 0 : (isDisabled ? 0.5 : 1),
                    pointerEvents: (isClicked || isDisabled) ? 'none' : 'auto'
                  }}
                >
                  {hoveredPresent === present.id ? present.emoji : '🎁'}
                </div>
              );
            })}

            {/* Camera in the center - always clickable after authentication */}
            <div
              className="present-box camera-box"
              onMouseEnter={() => setHoveredPresent('camera')}
              onMouseLeave={() => setHoveredPresent(null)}
              onClick={() => showPresents && navigate('/scrapbook')}
              style={{ cursor: showPresents ? 'pointer' : 'default' }}
            >
              {hoveredPresent === 'camera' ? '📸' : '🎁'}
            </div>

            {presents.slice(3).map((present) => {
              const isClicked = clickedPresents.includes(present.id);
              const isDisabled = timeRemaining !== null && timeRemaining > 0;
              return (
                <div
                  key={present.id}
                  className={`present-box ${isClicked ? 'clicked' : ''} ${isDisabled && !isClicked ? 'disabled' : ''}`}
                  onMouseEnter={() => !isClicked && !isDisabled && setHoveredPresent(present.id)}
                  onMouseLeave={() => setHoveredPresent(null)}
                  onClick={() => !isClicked && !isDisabled && handlePresentClick(present.id)}
                  style={{
                    opacity: isClicked ? 0 : (isDisabled ? 0.5 : 1),
                    pointerEvents: (isClicked || isDisabled) ? 'none' : 'auto'
                  }}
                >
                  {hoveredPresent === present.id ? present.emoji : '🎁'}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showGiftCard && (
        <GiftCardModal
          onClose={() => setShowGiftCard(false)}
          cardNumber="141462565567"
        />
      )}
    </div>
  );
}

export default App;
